// Невидимая капча форм заявок: Google reCAPTCHA v3 или Yandex SmartCaptcha — какая из них
// действует, задаёт сервер (CAPTCHA_PROVIDER, атрибут data-provider контейнера #captcha).
// Скрипт провайдера подгружается прогревом из app.js (пауза после первого взаимодействия
// или фокус в поле формы) либо по требованию перед отправкой формы; проверка запускается
// на каждый сабмит.

// Сколько ждём ответа капчи, пока посетителю не показано задание.
const RESPONSE_TIMEOUT_MS = 15000;
// «Задание закрыто» может прийти раньше success — ждём, прежде чем считать задание брошенным.
const CHALLENGE_CLOSE_GRACE_MS = 1000;

const getContainer = () => document.querySelector('#captcha');

const getSiteKey = () => getContainer()?.getAttribute('data-site-key');

const loadScript = (src, onLoad, resolve) => {
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.onload = onLoad;
  script.onerror = () => resolve(null);
  document.head.appendChild(script);
};

// Google reCAPTCHA v3: задание не показывает никогда, отдаёт токен с оценкой.
const recaptcha = {
  load: () => new Promise((resolve) => {
    loadScript(
      `https://www.google.com/recaptcha/api.js?render=${getSiteKey()}`,
      () => window.grecaptcha.ready(() => resolve(window.grecaptcha)),
      resolve,
    );
  }),

  check: (grecaptcha) => new Promise((resolve) => {
    const timer = setTimeout(() => resolve({captchaError: 'timeout'}), RESPONSE_TIMEOUT_MS);
    const finish = (fields) => {
      clearTimeout(timer);
      resolve(fields);
    };

    try {
      grecaptcha.execute(getSiteKey(), {action: 'submit'}).then(
        (token) => finish({recapchaToken: token}),
        () => finish({captchaError: 'javascript-error'}),
      );
    } catch (e) {
      finish({captchaError: 'javascript-error'});
    }
  }),
};

// Yandex SmartCaptcha: задание видит только посетитель, которого она сочла подозрительным.
let smartCaptchaWidgetId = null;

const smartcaptcha = {
  load: () => new Promise((resolve) => {
    window.onSmartCaptchaLoad = () => resolve(window.smartCaptcha);
    loadScript(
      'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=onSmartCaptchaLoad',
      null,
      resolve,
    );
  }),

  check: (smartCaptcha) => new Promise((resolve) => {
    let timer = null;
    let finished = false;
    const unsubscribers = [];

    const finish = (fields) => {
      if (finished) {
        return;
      }
      finished = true;
      clearTimeout(timer);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      // Токен одноразовый: следующая отправка должна получить новый.
      try {
        smartCaptcha.reset(smartCaptchaWidgetId);
      } catch (e) {
        // виджет мог не отрисоваться — сбрасывать нечего
      }
      resolve(fields);
    };

    try {
      if (smartCaptchaWidgetId === null) {
        smartCaptchaWidgetId = smartCaptcha.render(getContainer(), {
          sitekey: getSiteKey(),
          invisible: true,
          // Уведомление об обработке данных SmartCaptcha стоит в подвале сайта.
          hideShield: true,
          hl: 'ru',
        });
      }

      const on = (event, handler) => unsubscribers.push(
        smartCaptcha.subscribe(smartCaptchaWidgetId, event, handler)
      );

      on('success', (token) => finish({recapchaToken: token}));
      on('network-error', () => finish({captchaError: 'network-error'}));
      on('javascript-error', () => finish({captchaError: 'javascript-error'}));
      // Посетитель решает задание — торопить его таймаутом нельзя.
      on('challenge-visible', () => clearTimeout(timer));
      // Закрыл задание, не пройдя: ни токена, ни ошибки — сервер такую заявку не примет.
      on('challenge-hidden', () => {
        clearTimeout(timer);
        timer = setTimeout(() => finish({}), CHALLENGE_CLOSE_GRACE_MS);
      });

      timer = setTimeout(() => finish({captchaError: 'timeout'}), RESPONSE_TIMEOUT_MS);

      smartCaptcha.execute(smartCaptchaWidgetId);
    } catch (e) {
      finish({captchaError: 'javascript-error'});
    }
  }),
};

const getProvider = () => (
  getContainer()?.getAttribute('data-provider') === 'smartcaptcha' ? smartcaptcha : recaptcha
);

let loaderPromise = null;

export const loadCaptcha = () => {
  if (loaderPromise) {
    return loaderPromise;
  }

  if (!getSiteKey()) {
    return Promise.resolve(null);
  }

  loaderPromise = getProvider().load();

  return loaderPromise;
};

// Поля капчи для заявки:
//   {recapchaToken} — токен действующего провайдера (имя поля историческое, общее для обоих),
//                     сервер его проверит;
//   {captchaError}  — капча не сработала в браузере (скрипт не загрузился, сеть, ошибка
//                     JavaScript, таймаут): сервер примет заявку без проверки и пометит её;
//   {}              — ключ не задан или посетитель закрыл задание: сервер заявку отклонит.
export const getCaptchaFields = async () => {
  if (!getSiteKey()) {
    return {};
  }

  const api = await loadCaptcha();
  if (!api) {
    return {captchaError: 'load-error'};
  }

  return getProvider().check(api);
};

export const addCaptchaToken = async (obj) => {
  // Объект формы живёт между отправками — поля прошлой попытки остаться не должны.
  delete obj.recapchaToken;
  delete obj.captchaError;

  return Object.assign(obj, await getCaptchaFields());
};
